import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Typography, Button } from "@mui/material";
import { logout } from "../../apiCalls/auth";
import { useAuthStore } from "../../contexts/AuthContext";
import dayjs from "dayjs";
import LoadingSpinner from "../../components/reusable-components/LoadingSpinner";
import { useTrack } from "../../analytics/hooks";
import checkAccountValidity from "../../helpers/getAccountStatus";

export default function AccountNotActivePage({user}) {
    const {selectedAccount} = useAuthStore();
    const [validAccount, setValidAccount] = useState(false);
    const [loading, setLoading] = useState(false);
    const [renderMessage, setRenderMessage] = useState(<></>);
    const navigate = useNavigate();
    const track = useTrack();

    const isAccountValid = (account) => {
        if(!account) return false;
        const { isPendingToDelete, isTrialValid, isSubscriptionValid } = checkAccountValidity(account); 
        return !isPendingToDelete && (isTrialValid || isSubscriptionValid);
    };

    useEffect(() => {
        if(!selectedAccount) return;
        if(!isAccountValid(selectedAccount)){//if the account is not valid
            setValidAccount(false);
            setLoading(true);
            let count = 0;
            const interval = setInterval(async () => {
                if ((user?.Accounts[0] || user.account) && isAccountValid(user?.Accounts[0] || user.account) && count < 5) {
                    setValidAccount(true);
                    setLoading(false);
                    clearInterval(interval);
                } else if (!isAccountValid(user?.Accounts[0] || user.account) && count < 5) {
                    count++;
                }else if(count === 5){
                    //track the event
                    track("account_retrieval_error", {
                        user: user
                    });
                    setLoading(false);
                    clearInterval(interval);
                }
            }, 500);
        }else{
            setValidAccount(true);
        }
    }, [selectedAccount]);

    const calculateDaysToPurge = () => {
        if (selectedAccount.trashDate) {
            const trashDate = dayjs(selectedAccount.trashDate);
            const currentDate = dayjs();
            return 30 + trashDate.diff(currentDate, 'day');
        }
        return null;
    };

    useEffect(() => {
        if(!validAccount){
            if(user.viewContentStartOn){
                setRenderMessage(
                    <Grid
                        item
                        sx={{
                            width: "100%",
                            height: "67px",
                            marginBottom: "16px",
                        }}>
                        <Typography
                            sx={{
                                textAlign: "center",
                                font: "normal normal 400 12px/16px Lato",
                                letterSpacing: "0px",
                                color: "#100C08",
                            }}>
                            {!(selectedAccount.trashDate && selectedAccount.trashDate !== null) ? 
                                "We noticed that your access to Inno has ended, but your journey towards data-driven education doesn’t have to stop here. Renew or upgrade now to continue empowering education leaders with data-driven insights." :
                                `Your account has been scheduled for deletion. Your data will be permanently removed in ${calculateDaysToPurge()} days. If this was unintended, please contact our support team via the chat icon to request immediate restoration.`}
                        </Typography>
                    </Grid>
                )
            }else{
                if(loading){
                    setRenderMessage(
                        <Grid
                            item
                            sx={{
                                width: "100%",
                                height: "auto",
                                marginBottom: "16px",
                            }}>
                                <Grid
                                    item
                                    sx = {{
                                        width: "100%",
                                        height: "48px",
                                        padding: "10px 0px",
                                        marginBottom: "16px",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            textAlign: "center",
                                            font: "normal normal 400 12px/16px Lato",
                                            letterSpacing: "0px",
                                            color: "#100C08",
                                        }}>
                                        We're getting your account set up. This may take a moment—please hold tight while we complete the process.
                                    </Typography>
                                </Grid>
                                <Grid 
                                    item
                                    sx = {{
                                        width: "100%",
                                        height: "48px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <LoadingSpinner />
                                </Grid>
                        </Grid>
                    )
                }else{
                    setRenderMessage(
                        <Grid
                            item
                            sx={{
                                width: "100%",
                                height: "44px",
                                marginBottom: "16px",
                            }}>
                            <Typography
                                sx={{
                                    textAlign: "center",
                                    font: "normal normal 400 12px/16px Lato",
                                    letterSpacing: "0px",
                                    color: "#100C08",
                                }}>
                                Something didn’t go as expected. Please try logging out and back in. If the issue persists, reach out to our team through Intercom for assistance.
                            </Typography>
                        </Grid>
                    )
                }
            }
        }else{
            if(user.pendingToDelete){
                setRenderMessage(
                    <Grid
                        item
                        sx={{
                            width: "100%",
                            height: "67px",
                            marginBottom: "16px",
                        }}>
                        <Typography
                            sx={{
                                textAlign: "center",
                                font: "normal normal 400 12px/16px Lato",
                                letterSpacing: "0px",
                                color: "#100C08",
                            }}>
                            You’ve been removed from this account. Your data is scheduled for permanent deletion. If this was a mistake, please contact our support team via the chat icon to request restoration.
                        </Typography>
                    </Grid>
                )
            }else{
               navigate("/"); 
            }
        }
    }, [user, validAccount, loading]);
    
    const handleContact = () => {
        // // configure the company property
        // const company = {
        //     id: user.isSuperAdmin ? "Innovare" : user.Accounts && user.Accounts[0]?.id,
        //     name: user.isSuperAdmin ? "Innovare" : user.Accounts && user.Accounts[0]?.name,
        // };

        // // configure intercom settings in the window object
        // window.intercomSettings = {
        //     api_base: "https://api-iam.intercom.io",
        //     app_id: process.env.REACT_APP_INTERCOM_APPID,
        //     custom_launcher_selector:'#lauchIntercomButtonForExpiredAccount',
        //     name: user.fullName || user.email, // Full name
        //     user_id: user.id, // a UUID for your user
        //     email: user.email, // the email for your user
        //     created_at: user.createdAt, // Signup date as a Unix timestamp
        //     user_hash: user.intercomHash, // an Identity Verification user hash for your user,
        //     company: company
        // };

        // // intercom provided code snippet - appId inserted from env
        // (function (){
        //     var w=window;
        //     var ic=w.Intercom;
        //     if(typeof ic==="function"){
        //         ic('reattach_activator');
        //         ic('update', w.intercomSettings);
        //     }else{
        //         var d=document;
        //         var i=function(){i.c(arguments);};
        //         i.q=[];
        //         i.c=function(args){i.q.push(args);};
        //         w.Intercom=i;
        //         var l=function(){
        //                 var s=d.createElement('script');
        //                 s.type='text/javascript';
        //                 s.async=true;s.src=`https://widget.intercom.io/widget/${process.env.REACT_APP_INTERCOM_APPID}`;
        //                 var x=d.getElementsByTagName('script')[0];
        //                 x.parentNode.insertBefore(s,x);
        //             };
        //         if(document.readyState==='complete'){
        //             l();
        //         }else if(w.attachEvent){
        //             w.attachEvent('onload',l);
        //         }else{
        //             w.addEventListener('load',l,false);
        //         }
        //     }
        // })();
    }
    const handleLogOut = () => {
        logout()
    };

    return (
        <Grid
            container
            sx={{
                width: "100%",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
            }}>
            <Grid
                item
                sx={{
                    width: "365px",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "4px",
                }}>
                <Grid container sx={{
                    width: "100%",
                    height: "100%",
                    padding: "28px 26px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                }}>
                    {renderMessage}
                    <Grid 
                        item
                        sx = {{
                            width: "100%",
                            height: "30px"
                        }}
                    >
                        <Grid
                            container
                            sx = {{
                                width: "100%",
                                height: "100%",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}
                        >
                            <Grid
                                sx={{
                                    width: "152px",
                                    height: "30px",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}>
                                <Button
                                    id="lauchIntercomButtonForExpiredAccount"
                                    onClick={handleContact}
                                    fullWidth
                                    sx={{
                                        backgroundColor: "#AC5CCC",
                                        "&:hover": { backgroundColor: "#963ABB" },
                                        textTransform: "none",
                                        padding: "5px 20px",
                                        color: "var(--Neutrals-100, #FFF)",
                                        borderRadius: "6px"
                                    }}>
                                    <Typography
                                        sx={{
                                            textAlign: "center",
                                            fontFamily: "Lato",
                                            fontSize: "17px",
                                            fontStyle: "normal",
                                            fontWeight: 700,
                                            lineHeight: "normal",
                                        }}>
                                        Contact Us
                                    </Typography>
                                </Button>
                            </Grid>
                            <Grid
                                item
                                sx={{
                                    width: "152px",
                                    height: "30px",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}>
                                <Button
                                    id="logoutButton"
                                    onClick={handleLogOut}
                                    fullWidth
                                    sx={{
                                        backgroundColor: "#FFFFFF",
                                        "&:hover": { 
                                            backgroundColor: "var(--Primary-100, #16124C)",
                                            color: "var(--Neutrals-100, #FFF)"
                                        },
                                        textTransform: "none",
                                        padding: "5px 20px",
                                        borderRadius: "6px",
                                        color: "var(--Primary-500, #16124C)",
                                    }}>
                                    <Typography
                                        sx={{
                                            textAlign: "center",
                                            fontFamily: "Lato",
                                            fontSize: "17px",
                                            fontStyle: "normal",
                                            fontWeight: 700,
                                            lineHeight: "normal",
                                        }}>
                                        Logout
                                    </Typography>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>  
                </Grid>
            </Grid>
        </Grid>
    );
}
